import { FC, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Tooltip, message } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { mediaPlanUtil, useDeleteChannelMutation } from '@/api';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import type { ActionsProps } from './Actions.types';
import { APP_SCOPE, NAMESPACE, TAG, DELETE_ACTION_COLOR } from '@/constants';
import { useRole } from '@/hooks/useRole';
import { useAppDispatch } from '@/hooks/useAppDispatch';

/**
 * Row actions for a line item: edit, duplicate and delete, each shown only to
 * roles allowed to do it. Duplicate saves a new line, so it needs create
 * rights rather than update.
 *
 * On a booked plan, delete becomes a removal: it asks why, takes the line off
 * the booking sheet too, and leaves a record of it on the plan — the signed IO
 * is untouched and no new one is raised.
 */
const Actions: FC<ActionsProps> = ({ id, isBooked, edit, duplicate }) => {
  const dispatch = useAppDispatch();
  const { canCreate, canDelete, canUpdate } = useRole(APP_SCOPE.MEDIA_PLAN);
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [deleteChannel, { isLoading: isDeleting }] = useDeleteChannelMutation();
  const [isRemoveOpen, setRemoveOpen] = useState(false);
  const [form] = Form.useForm<{ reason: string }>();

  const remove = async (reason?: string) => {
    try {
      await deleteChannel({ id, reason }).unwrap();
      dispatch(mediaPlanUtil.invalidateTags([TAG.MEDIA_PLAN]));
      message.success(t(reason ? 'lineItemRemoved' : 'channelHasBeenDeleted'));
      setRemoveOpen(false);
      form.resetFields();
    } catch (err) {
      /* The global toast skips this endpoint, so say what went wrong here —
         the API's message explains whether anything was changed. */
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || t('lineItemRemoveFailed'), 8);
    }
  };

  const { handleConfirm: handleDeleteChannel } = useConfirmModal({
    title: t('deleteChannelModalTitle'),
    content: t('deleteChannelModalDescription'),
    destructive: true,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('delete', { ns: NAMESPACE.COMMON }),
    onCancel: () => remove()
  });

  return (
    <>
      {/* Icons rather than words: the column is pinned to the right edge so
          the actions never need a scroll to reach, and three text buttons did
          not fit in it — the last one was clipped off the end of the cell. */}
      <Space size={4}>
        {canUpdate && (
          <Tooltip title={t('edit', { ns: NAMESPACE.COMMON })}>
            <Button
              onClick={edit}
              type="text"
              icon={<EditOutlined />}
              aria-label={t('edit', { ns: NAMESPACE.COMMON })}
            />
          </Tooltip>
        )}
        {canCreate && (
          <Tooltip title={t('duplicate', { ns: NAMESPACE.COMMON })}>
            <Button
              onClick={duplicate}
              type="text"
              icon={<CopyOutlined />}
              aria-label={t('duplicate', { ns: NAMESPACE.COMMON })}
            />
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title={isBooked ? t('removeLineItem') : t('delete', { ns: NAMESPACE.COMMON })}>
            <Button
              onClick={isBooked ? () => setRemoveOpen(true) : handleDeleteChannel}
              type="text"
              danger
              icon={<DeleteOutlined />}
              style={{ color: DELETE_ACTION_COLOR }}
              aria-label={isBooked ? t('removeLineItem') : t('delete', { ns: NAMESPACE.COMMON })}
            />
          </Tooltip>
        )}
      </Space>

      <Modal
        open={isRemoveOpen}
        title={t('removeLineItemTitle')}
        okText={t('removeLineItemConfirm')}
        okButtonProps={{ danger: true }}
        confirmLoading={isDeleting}
        onOk={async () => {
          const { reason } = await form.validateFields();
          await remove(reason.trim());
        }}
        onCancel={() => {
          setRemoveOpen(false);
          form.resetFields();
        }}
        destroyOnHidden
      >
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('removeLineItemHint')} />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="reason"
            label={t('removeLineItemReason')}
            rules={[{ required: true, whitespace: true, message: t('removeLineItemReasonRequired') }]}
          >
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Actions;
